import { useState } from 'react';
import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { DialogContextProvider } from '@xing-com/crate-entity-pages-entity-pages-shared-context-dialog-context';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { ModalPage } from '@xing-com/modal-page';

import type {
  AboutUsSubpageFactsFragment_CompanyAboutUsFacts_Fragment,
  AboutUsSubpageFactsFragment_PublisherAboutUsFacts_Fragment,
} from '../../graphql/fragment/about-us-subpage-facts-fragment.gql-types';

import type { FactsItemProps } from './facts-item';
import { FactsItem } from './facts-item';
import * as Styled from './facts.styles';

export type FactsProps = {
  headlineKey: string;
  items: Array<FactsItemProps & { omViewContent?: React.JSX.Element | false }>;
  categoriesHeadlineKey: string;
  categories: string[];
  facts:
    | AboutUsSubpageFactsFragment_PublisherAboutUsFacts_Fragment
    | AboutUsSubpageFactsFragment_CompanyAboutUsFacts_Fragment;
};

export const Facts: FC<FactsProps> = ({
  headlineKey,
  items,
  categoriesHeadlineKey,
  categories,
}) => {
  const isEditing = useEditStore(({ isEditing }) => isEditing);
  const { pageContext } = usePageContext();

  const [factViewSelected, setFactViewSelected] = useState<
    React.JSX.Element | false | undefined
  >(undefined);

  if (!(items?.length > 0 || (categories && categories?.length > 0))) {
    return null;
  }

  return (
    <div>
      <Styled.FactsContainer>
        {items?.length > 0 && (
          <>
            <div>
              <Styled.FactsHeadline size={'large'} sizeWide={'xxlarge'}>
                <FormattedMessage
                  id={headlineKey}
                  defaultMessage={headlineKey}
                />
              </Styled.FactsHeadline>
              {isEditing && (
                <DialogContextProvider>
                  <Styled.FactsEditButton
                    size={'small'}
                    showIcon={true}
                    to={`${pageContext.basePath}/${pageContext.pageSlug}/edit/facts`}
                    data-testid="edit-facts"
                  />
                </DialogContextProvider>
              )}
            </div>
            <Styled.FactsList>
              {items.map(({ omViewContent, ...props }, index) => (
                <li key={index}>
                  <FactsItem
                    {...{
                      ...props,
                      onClick: () => setFactViewSelected(omViewContent),
                    }}
                  />
                </li>
              ))}
            </Styled.FactsList>
            {factViewSelected ? (
              <ModalPage
                isShown
                closeButton={{
                  onClick: () => setFactViewSelected(undefined),
                }}
                isAnimated
                noGrid
              >
                <Styled.FactsModalWrapper>
                  {factViewSelected}
                </Styled.FactsModalWrapper>
              </ModalPage>
            ) : null}
          </>
        )}
        {categories && categories.length > 0 && (
          <>
            <Styled.CategoriesHeadline size={'large'} sizeWide={'xxlarge'}>
              <FormattedMessage
                id={categoriesHeadlineKey}
                defaultMessage={categoriesHeadlineKey}
              />
            </Styled.CategoriesHeadline>
            {isEditing && (
              <DialogContextProvider>
                <Styled.FactsEditButton
                  size={'small'}
                  showIcon={true}
                  to={`${pageContext.basePath}/${pageContext.pageSlug}/edit/facts?type=industries`}
                  data-testid="edit-categories"
                />
              </DialogContextProvider>
            )}
          </>
        )}
        <Styled.CategoriesList data-testid="categories-list">
          {categories?.map((item: string) => (
            <li key={item}>
              <Styled.FactsTag>{item}</Styled.FactsTag>
            </li>
          ))}
        </Styled.CategoriesList>
      </Styled.FactsContainer>
    </div>
  );
};
